import React, { createContext, useContext, useState, useEffect } from "react";

const CartContext = createContext();

export const CartProvider = ({ children }) => {
  // Initialize state directly from localStorage
  const [cart, setCart] = useState(() => {
    try {
      const savedCart = localStorage.getItem("beeluxe_cart");
      return savedCart ? JSON.parse(savedCart) : [];
    } catch (err) {
      console.error("Error reading cart from localStorage:", err);
      return [];
    }
  });

  // Persist state updates to localStorage
  useEffect(() => {
    try {
      localStorage.setItem("beeluxe_cart", JSON.stringify(cart));
    } catch (err) {
      console.error("Error saving cart to localStorage:", err);
    }
  }, [cart]);

  // Add item (supports custom quantities)
  const addToCart = (product, qtyToAdd = 1) => {
    setCart((prevCart) => {
      const addAmount = product.quantity || qtyToAdd || 1;
      const existingItem = prevCart.find((item) => item.id === product.id);

      if (existingItem) {
        return prevCart.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + addAmount }
            : item,
        );
      }

      return [...prevCart, { ...product, quantity: addAmount }];
    });
  };

  // Remove an item entirely
  const removeFromCart = (id) => {
    setCart((prevCart) => prevCart.filter((item) => item.id !== id));
  };

  // Update exact item quantity
  const updateQuantity = (id, quantity) => {
    if (quantity < 1) {
      removeFromCart(id);
      return;
    }

    setCart((prevCart) =>
      prevCart.map((item) => (item.id === id ? { ...item, quantity } : item)),
    );
  };

  // Empty cart
  const clearCart = () => setCart([]);

  // Derived calculations
  const cartTotal = cart.reduce(
    (total, item) => total + Number(item.price) * item.quantity,
    0,
  );

  const itemCount = cart.reduce((count, item) => count + item.quantity, 0);

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        cartTotal,
        itemCount,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error("useCart must be used within a CartProvider");
  }
  return context;
};
